/* eslint-disable max-lines */
/* eslint-disable complexity */
/* eslint-disable react/jsx-key */
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { XboxSpinner } from "@gamedev-web/gamedev-sdk-core";
import { BasicButton, ButtonType } from "@gaming/xds-react-js";

import { ContentBox, ContentBoxIcon, DeleteButton, ViewButton } from "../";
import { DefaultNewGameConceptId } from "../../constants";
import { GameConceptIdSearchParam } from "../../helpers/dataFetchingHelpers";
import { MaxStartedApplications, useAmountOfStartedApplications } from "../../helpers/formsHelpers";
import {
  GameConceptApplication,
  isGameConceptHardRejected,
  useCategorizedGameConcepts,
} from "../../helpers/gameConceptHelpers";
import { getDashboardUrl } from "../../helpers/navigationHelpers";
import { useStrings } from "../../localization/localizationHelpers";
import { CrmGameConceptStatus, CrmGameConceptSubStatus } from "../../models/crmStatus";
import { Programs } from "../../models/programsConfig";
import { RouteNames } from "../../partnerConfig/routeNames";
import { OnboardingUrls } from "../../partnerConfig/routes";
import { CrmActions } from "../../redux/actions/crmApiActions";
import { getCurrentProgram } from "../../redux/selectors";
import { OnboardingPageAction } from "../../telemetry/models";
import { logPageAction } from "../../telemetry/pageAction";
import * as commonStyles from "../commonStyles/commonStyles.module.css";
import * as styles from "./GameConceptsList.module.css";
import { XPAInitiativesProgramRow } from "./XPAInitiativesProgramRow";

export type GameConceptsListProps = Readonly<{
  programDashboardRouteName: RouteNames;
  removeStartedApplicationsLimit?: boolean;
}>;

type GameConceptRowProps = Readonly<{
  programDashboardRouteName: RouteNames;
  gameConcept: GameConceptApplication;
  _key: string | number;
}>;

export enum ProgramDisplayStatus {
  Draft = "Draft",
  Enrolled = "Enrolled",
  NotEligible = "NotEligible",
  Block = "Block",
}

const GameConceptRow = ({ gameConcept, programDashboardRouteName, _key }: GameConceptRowProps) => {
  const [
    untitledGame,
    draft,
    underReview,
    additionalContractsSent,
    rejected,
    approved,
    continueApplication,
    needMoreInformation,
    editToResubmit,
    additionalReviewRequired,
  ] = useStrings(
    "untitledGame",
    "draft",
    "underReview",
    "additionalContractsSent",
    "rejected",
    "approved",
    "continueApplication",
    "needMoreInformation",
    "editToResubmit",
    "additionalReviewRequired"
  );
  const history = useHistory();
  const dispatch = useDispatch();
  const gameConceptInfo = gameConcept.gameConceptInfo;
  const gameState = gameConceptInfo.state;
  const gameStatus = gameState.status;
  const gameSubstatus = gameState.subStatus;
  const gameTitle = gameConceptInfo.details.gameTitle;
  const currentProgram = useSelector(getCurrentProgram);
  const [isDeleting, setIsDeleting] = useState(false);

  let statusMessage = underReview;
  switch (gameStatus) {
    case CrmGameConceptStatus.Unset:
      statusMessage = draft;
      break;
    case CrmGameConceptStatus.UnderReview:
      statusMessage = underReview;
      break;
    case CrmGameConceptStatus.AdditionalContractsSent:
      statusMessage = additionalContractsSent;
      break;
    case CrmGameConceptStatus.Accepted:
      statusMessage = approved;
      break;
    case CrmGameConceptStatus.Rejected:
      statusMessage = rejected;
      break;
    case CrmGameConceptStatus.MoreInfoNeeded:
      statusMessage = needMoreInformation;
      break;
    case CrmGameConceptStatus.OnHold:
      statusMessage = additionalReviewRequired;
      break;
  }

  const viewOnlyGame =
    gameStatus === CrmGameConceptStatus.UnderReview ||
    gameStatus === CrmGameConceptStatus.AdditionalContractsSent ||
    gameStatus === CrmGameConceptStatus.Accepted ||
    gameStatus === CrmGameConceptStatus.OnHold ||
    isGameConceptHardRejected(gameStatus, gameSubstatus);

  const continueButtonOnClick = () => {
    logPageAction(OnboardingPageAction.ContinueApplicationButton);
    // TODO: Investigate how to make this work client-side without breaking Fast-Track
    // and other edge cases
    history.push(getDashboardUrl(programDashboardRouteName, gameState.id));
  };

  const deleteButtonOnClick = () => {
    logPageAction(OnboardingPageAction.DeleteConceptButton);
    setIsDeleting(true);
    dispatch({
      type: CrmActions.DELETE_GAME_CONCEPT_START,
      program: currentProgram,
      gameConceptId: gameConcept.id,
    });
  };

  const viewApplicationLinkOnClick = () => {
    logPageAction(OnboardingPageAction.ViewGameConceptLink);
    history.push({
      pathname:
        OnboardingUrls[
          currentProgram === Programs.Aaa ? RouteNames.AaaFormPreview : RouteNames.IdXboxFormPreview
        ],
      search: `?${GameConceptIdSearchParam}=${gameConcept.id}`,
    });
  };

  let continueButtonText = "";
  if (!viewOnlyGame) {
    switch (gameStatus) {
      case CrmGameConceptStatus.MoreInfoNeeded:
        continueButtonText = needMoreInformation;
        break;
      case CrmGameConceptStatus.Rejected:
        if (
          gameSubstatus === CrmGameConceptSubStatus.RejectedSoftNeedsDevToken ||
          gameSubstatus === CrmGameConceptSubStatus.RejectedSoftAlreadyHaveDevToken ||
          gameSubstatus === CrmGameConceptSubStatus.RejectedSoftGeneral
        ) {
          continueButtonText = editToResubmit;
        }
        break;
      default:
        continueButtonText = continueApplication;
        break;
    }
  }

  return (
    <div key={_key} className={commonStyles.rowContainer}>
      <div className={commonStyles.titleStatusContainer}>
        <h3 className={commonStyles.gameTitle}>{gameTitle !== "" ? gameTitle : untitledGame}</h3>
        <div className={styles.applicationState}>{statusMessage}</div>
        <XPAInitiativesProgramRow gameConcept={gameConcept} />
      </div>

      {isDeleting && <XboxSpinner size={40} />}
      {!isDeleting && (
        <>
          {!viewOnlyGame && (
            <>
              <BasicButton
                as={"button"}
                buttonType={ButtonType.Secondary}
                className={styles.continueButton}
                onClick={continueButtonOnClick}
                role="link"
              >
                {continueButtonText}
              </BasicButton>
              {gameStatus === CrmGameConceptStatus.Unset && (
                <DeleteButton onClick={deleteButtonOnClick} />
              )}
            </>
          )}
          {viewOnlyGame && <ViewButton onClick={viewApplicationLinkOnClick} />}
        </>
      )}
    </div>
  );
};
const sortGameConcepts = (gameConcepts?: GameConceptApplication[]) => {
  if (gameConcepts && gameConcepts.length > 0) {
    gameConcepts.sort((game1, game2) =>
      (game1.gameConceptInfo.details.gameTitle ?? "").localeCompare(
        game2.gameConceptInfo.details.gameTitle ?? ""
      )
    );
  }
};

export const GameConceptsList = ({
  programDashboardRouteName,
  removeStartedApplicationsLimit,
}: GameConceptsListProps) => {
  const [myGameApplication, newApplication, welcomePageGameConceptsListEmpty] = useStrings(
    "myGameApplication",
    "newApplication",
    "welcomePageGameConceptsListEmpty"
  );
  const gameConcepts = useCategorizedGameConcepts();
  const amountOfStartedApplications = useAmountOfStartedApplications();
  const history = useHistory();

  const incompleteGames = gameConcepts?.incompleteApplications;
  const incompleteReadOnlyGames = gameConcepts?.incompleteReadOnlyApplications;
  const completeGames = gameConcepts?.completeApplications;
  const rejectedGames = gameConcepts?.rejectedApplications;

  const noGameConcepts =
    (!incompleteGames || incompleteGames.length === 0) &&
    (!incompleteReadOnlyGames || incompleteReadOnlyGames.length === 0) &&
    (!completeGames || completeGames.length === 0) &&
    (!rejectedGames || rejectedGames.length === 0);

  sortGameConcepts(incompleteGames);
  sortGameConcepts(incompleteReadOnlyGames);
  sortGameConcepts(completeGames);
  sortGameConcepts(rejectedGames);

  const showNewApplicationLink =
    removeStartedApplicationsLimit ||
    (amountOfStartedApplications !== undefined &&
      amountOfStartedApplications < MaxStartedApplications);

  return (
    <ContentBox
      titleConfiguration={{
        title: myGameApplication,
        rightSideLink: showNewApplicationLink
          ? {
              text: newApplication,
              onClick: () =>
                history.push(getDashboardUrl(programDashboardRouteName, DefaultNewGameConceptId)),
              icon: ContentBoxIcon.Add,
            }
          : undefined,
      }}
    >
      {noGameConcepts && (
        <p className={styles.noGameConceptsMessage}>{welcomePageGameConceptsListEmpty}</p>
      )}
      {incompleteGames?.map((gameConcept, index) => (
        <GameConceptRow
          gameConcept={gameConcept}
          programDashboardRouteName={programDashboardRouteName}
          key={`${index}-${gameConcept.id}`}
          _key={`${index}-${gameConcept.id}`}
        />
      ))}
      {incompleteReadOnlyGames?.map((gameConcept, index) => (
        <GameConceptRow
          gameConcept={gameConcept}
          programDashboardRouteName={programDashboardRouteName}
          key={`${index}-${gameConcept.id}`}
          _key={`${index}-${gameConcept.id}`}
        />
      ))}
      {completeGames?.map((gameConcept, index) => (
        <GameConceptRow
          gameConcept={gameConcept}
          programDashboardRouteName={programDashboardRouteName}
          key={`${index}-${gameConcept.id}`}
          _key={`${index}-${gameConcept.id}`}
        />
      ))}
      {rejectedGames?.map((gameConcept, index) => (
        <GameConceptRow
          gameConcept={gameConcept}
          programDashboardRouteName={programDashboardRouteName}
          key={`${index}-${gameConcept.id}`}
          _key={`${index}-${gameConcept.id}`}
        />
      ))}
    </ContentBox>
  );
};
